import React, { useEffect, useState } from 'react'
import { Alert, Button, Col, Form, Modal, Row, Table } from '@themesberg/react-bootstrap'
import { getGameCutoffPolicy, saveGameCutoffPolicyDraft } from '../../../../../controller/api/game'
import { draftDeadline, formToOffset, legacyOffsetSuggestion, offsetToForm } from './cutoff-policy-form'

const emptyField = { value: '', unit: 'seconds' }

function OffsetField({ label, field, onChange, disabled }) {
  return (
    <Form.Group className="mb-3">
      <Form.Label>{label} — trước giờ xổ</Form.Label>
      <Row className="g-2">
        <Col xs={7}><Form.Control aria-label={label} type="number" min="0" step="1" value={field.value}
          disabled={disabled} onChange={event => onChange({ ...field, value: event.target.value })} /></Col>
        <Col xs={5}><Form.Select aria-label={`Đơn vị ${label}`} value={field.unit} disabled={disabled}
          onChange={event => onChange({ ...field, unit: event.target.value })}>
          <option value="seconds">Giây</option><option value="minutes">Phút</option>
        </Form.Select></Col>
      </Row>
    </Form.Group>
  )
}

export default function CutoffPolicyDraft({ game, show, onHide, token, onSaved }) {
  const [data, setData] = useState(null)
  const [sale, setSale] = useState(emptyField)
  const [print, setPrint] = useState(emptyField)
  const [upload, setUpload] = useState(emptyField)
  const [uploadMode, setUploadMode] = useState('UNLIMITED')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)
  const gameId = game?.id

  useEffect(() => {
    if (!show || !gameId) return undefined
    let cancelled = false
    setLoading(true); setData(null); setError(''); setSuccess('')
    getGameCutoffPolicy(token, gameId).then(result => {
      if (cancelled) return
      if (result.status !== 1 || !result.policy) throw new Error(result.message || 'Không tải được cấu hình')
      setData(result)
      const preferMinutes = gameId !== 'keno'
      setSale(offsetToForm(result.policy.saleCutoffOffsetSeconds ?? legacyOffsetSuggestion(result.periods, 'saleCutoffAt'), preferMinutes))
      setPrint(offsetToForm(result.policy.printCutoffOffsetSeconds ?? legacyOffsetSuggestion(result.periods, 'printCutoffAt'), preferMinutes))
      setUploadMode(gameId === 'keno' ? 'BEFORE_DRAW' : result.policy.uploadCutoffMode || 'UNLIMITED')
      setUpload(offsetToForm(result.policy.uploadCutoffOffsetSeconds ?? (gameId === 'keno' ? 1 : null), preferMinutes))
    }).catch(failure => {
      if (!cancelled) setError(failure.response?.data?.message || failure.message || 'Không tải được cấu hình')
    }).finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [show, gameId, token, refreshKey])

  const save = async () => {
    setError(''); setSuccess('')
    try {
      const payload = {
        id: gameId, expectedVersion: data.policy.version, apply: true,
        saleCutoffOffsetSeconds: formToOffset(sale, 'Đóng mua'),
        printCutoffOffsetSeconds: formToOffset(print, 'Đóng in'),
        uploadCutoffMode: uploadMode,
        uploadCutoffOffsetSeconds: uploadMode === 'UNLIMITED' ? null : formToOffset(upload, 'Đóng upload'),
      }
      setSaving(true)
      const result = await saveGameCutoffPolicyDraft(token, payload)
      if (result.status !== 1 || !result.policy || result.policy.enabled !== true) {
        throw new Error(result.message || 'Không xác nhận được cấu hình đã áp dụng')
      }
      setData(current => ({ ...current, policy: result.policy }))
      setSuccess(`Đã lưu và áp dụng ba mốc v${result.policy.version}.`)
      if (onSaved) await onSaved()
    } catch (failure) {
      setError(failure.response?.data?.message || failure.message || 'Không lưu được cấu hình')
    } finally { setSaving(false) }
  }

  const disabled = loading || saving || !data || data.activationAvailable !== true
  return (
    <Modal className="game-cutoff-draft" show={show} onHide={() => { if (!saving) onHide() }} size="lg" centered scrollable backdrop={saving ? 'static' : true} keyboard={!saving}>
      <Modal.Header closeButton={!saving}><Modal.Title>Cấu hình 3 mốc — {game?.name || gameId}</Modal.Title></Modal.Header>
      <Modal.Body>
        <Alert variant="warning">
          <strong>Ba mốc độc lập.</strong> Lưu sẽ áp dụng cho thao tác tiếp theo, kể cả đơn đang chờ. Admin cần kiểm tra giờ máy Vietlott đóng in và thời gian dự phòng trước khi lưu.
        </Alert>
        {loading && <p>Đang tải cấu hình…</p>}
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        {data && <>
          {data.activationAvailable !== true && <Alert variant="info">
            Backend hiện chưa hỗ trợ áp dụng 3 mốc. Cần cập nhật backend và kiểm tra migration trước; cấu hình đang chạy vẫn giữ nguyên.
          </Alert>}
          <p className="text-muted">Phiên bản: {data.policy.version}. Các giá trị chưa lưu được gợi ý từ kỳ mẫu khi đủ dữ liệu; admin cần kiểm tra trước khi lưu.</p>
          <div className="game-cutoff-form">
          <section aria-label="Cấu hình đóng mua"><h6>1. Đóng mua</h6>
          <p className="text-muted">Khách ngừng mua / thanh toán vé cho kỳ đã chọn.</p>
          <OffsetField label="Đóng mua" field={sale} onChange={setSale} disabled={disabled} />
          {gameId === 'keno' && <p className="text-muted">Keno: đóng mua từ 0 đến 420 giây trước giờ xổ, theo giới hạn đang dùng.</p>}
          </section>
          <section aria-label="Cấu hình đóng in"><h6>2. Đóng in</h6>
          <p className="text-muted">Nhân viên ngừng gửi lệnh in mới cho kỳ đó.</p>
          <OffsetField label="Đóng in" field={print} onChange={setPrint} disabled={disabled} />
          <p className="text-muted">Đóng in là mốc cuối hệ thống được phép bắt đầu in; admin tự tính giờ máy Vietlott đóng và thời gian dự phòng.</p>
          </section>
          <section aria-label="Cấu hình đóng upload"><h6>3. Đóng upload vé</h6>
          <p className="text-muted">Ngừng gắn ảnh vé / xác nhận hoàn thành đơn.</p>
          <Form.Group className="mb-3">
            <Form.Label>Đóng upload vé / hoàn thành</Form.Label>
            <Form.Select aria-label="Giới hạn upload vé" value={uploadMode} disabled={disabled} onChange={event => setUploadMode(event.target.value)}>
              {gameId !== 'keno' && <option value="UNLIMITED">Không giới hạn</option>}
              <option value="BEFORE_DRAW">Trước giờ xổ</option>
            </Form.Select>
          </Form.Group>
          {uploadMode === 'BEFORE_DRAW' && <OffsetField label="Đóng upload" field={upload} onChange={setUpload} disabled={disabled} />}
          <p className="text-muted">Hết giờ in không đồng nghĩa hết giờ upload. Upload/hoàn thành luôn giữ đúng kỳ thực tế đã in.</p>
          </section>
          </div>
          <h6>Xem trước — giờ Việt Nam</h6>
          {data.periods.length === 0 ? <Alert variant="info">Chưa có kỳ tương lai để xem trước. Không tự suy ra giờ quay từ ngày hoặc tên kỳ.</Alert> :
            <Table responsive size="sm">
              <thead><tr><th>Kỳ / giờ xổ</th><th>Đóng mua</th><th>Đóng in</th><th>Đóng upload</th></tr></thead>
              <tbody>{data.periods.map(period => <tr key={period.id}>
                <td>{period.name}<br />{draftDeadline(period.dialDate, { value: '0', unit: 'seconds' })}</td>
                <td>{draftDeadline(period.dialDate, sale)}</td>
                <td>{draftDeadline(period.dialDate, print)}</td>
                <td>{draftDeadline(period.dialDate, upload, uploadMode === 'UNLIMITED')}</td>
              </tr>)}</tbody>
            </Table>}
          <details>
            <summary>Đối chiếu giờ đang vận hành (legacy)</summary>
            <p className="text-muted mt-2">Cấu hình cũ dùng khi chưa áp dụng ba mốc mới. Khoảng chờ terminal vẫn giữ nguyên; preview không phải bằng chứng máy đã in.</p>
            {data.periods.map(period => <p key={period.id}>
              {period.name}: mua {period.legacy.saleCutoffAt ? draftDeadline(period.legacy.saleCutoffAt, { value: '0', unit: 'seconds' }) : 'chưa xác định'};
              {' '}in {period.legacy.printCutoffAt ? draftDeadline(period.legacy.printCutoffAt, { value: '0', unit: 'seconds' }) : 'chưa xác định'}.
            </p>)}
          </details>
        </>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" disabled={saving || loading} onClick={() => setRefreshKey(value => value + 1)}>Tải lại</Button>
        <Button variant="secondary" disabled={saving} onClick={onHide}>Đóng</Button>
        <Button variant="primary" disabled={disabled} onClick={save}>{saving ? 'Đang lưu…' : 'Lưu và áp dụng 3 mốc'}</Button>
      </Modal.Footer>
    </Modal>
  )
}
